import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {axiosInstance} from '../routes/axiosInstance';
import moment from "moment/moment";
import {appendLatestSessionOnlyParam, buildHorizonCompareQuery} from './forecastQueryUtils';

const formatHorizonLabel = (horizonOffset, challengeType) => {
  if (challengeType === 'intraday' || Number(horizonOffset) === 0) {
    return 'Intraday';
  }
  return `D+${horizonOffset}`;
};

// Helper function to get preferred model from localStorage
const getPreferredModel = () => {
  try {
    const prefs = localStorage.getItem('userPreferences');
    if (prefs) {
      const parsed = JSON.parse(prefs);
      return parsed.preferredModel || 'weighted_avg';
    }
  } catch (err) {
    console.error('Error reading preferred model:', err);
  }
  return 'weighted_avg';
};

export const fetchAvailableHorizons = createAsyncThunk(
  'market/fetchAvailableHorizons',
  async ({ resourceId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/data/available-horizons/?resource=${resourceId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch available horizons.');
    }
  }
);

export const fetchMarketResources = createAsyncThunk(
  'market/fetchMarketResources',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/user/resource/');
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch market resources.');
    }
  }
);

export const fetchSessionForecastSubmission = createAsyncThunk(
  'market/fetchSessionForecastSubmission',
  async ({challengeId, userId}, {rejectWithValue}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('challenge', challengeId);
      queryParams.append('user', userId);
      const query = `?${queryParams.toString()}`;

      const response = await axiosInstance.get(`/market/challenge/submission/forecasts${query}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch session forecast submission.');
    }
  }
);

export const fetchChallengeSubmissions = createAsyncThunk(
  'market/fetchChallengeSubmissions',
  async ({ challengeIds }, { rejectWithValue }) => {
    try {
      const results = await Promise.all(
        challengeIds.map(async (challengeId) => {
          const response = await axiosInstance.get(
            `/market/challenge/submission/?challenge=${challengeId}`
          );
          const submissions = response.data?.data || [];
          const quantiles = [...new Set(submissions.map((s) => s.variable))].sort();
          return { challengeId, quantiles, submitted: submissions.length > 0 };
        })
      );
      return results;
    } catch (error) {
      return rejectWithValue('Failed to fetch challenge submissions.');
    }
  }
);

export const fetchMarketSessionChallenge = createAsyncThunk(
  'market/fetchMarketSessionChallenge',
  async ({marketSessionId, resourceId, openOnly, useCase, startDate, endDate}, {rejectWithValue}) => {
    try {
      // Initialize query parameters array
      const queryParams = [];

      // Add parameters to the query string only if they are provided
      if (marketSessionId) queryParams.push(`market_session=${marketSessionId}`);
      if (resourceId) queryParams.push(`resource=${resourceId}`);
      if (openOnly) queryParams.push(`open_only=${openOnly}`);
      if (useCase) queryParams.push(`use_case=${useCase}`);
      if (startDate) queryParams.push(`start_date=${startDate}`);
      if (endDate) queryParams.push(`end_date=${endDate}`);

      // Join the parameters into a query string
      const query = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

      // Perform the API call
      const response = await axiosInstance.get(`/market/challenge/${query}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch challenge ID.');
    }
  }
);
// Thunk to fetch session or market scoreboard
export const fetchScoreboard = createAsyncThunk(
  'market/fetchScoreboard',
  async ({resource, latestWithResults = true, marketSessionId = null}, {rejectWithValue}) => {

    try {
      const params = new URLSearchParams();

      if (marketSessionId !== null) {
        params.set('market_session', marketSessionId);
      } else if (latestWithResults) {
        params.set('latest_with_results', 'true');
      }

      params.set('resource', resource);

      const response = await axiosInstance.get(`/market/leaderboard/scores?${params.toString()}`);
      return response.data;
    } catch (error) {
      // Optionally, extract more detailed error information
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch session scoreboard.';
      return rejectWithValue(errorMessage);
    }
  }
);

// Thunk to fetch ensemble forecasts
export const fetchEnsembleForecasts = createAsyncThunk(
  'market/fetchEnsembleForecasts',
  async ({challengeId, model = 'weighted_avg'}, {rejectWithValue}) => {
    try {
      const query = `?challenge=${challengeId}&model=${model}`;
      const response = await axiosInstance.get(
        `/market/challenge/ensemble-forecasts${query}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch ensemble forecasts.');
    }
  }
);

export const fetchLatestMarketSession = createAsyncThunk(
  'market/fetchLatestMarketSession',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/market/session/?latest_only=true');
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch latest market session.');
    }
  }
);

export const fetchSubmissionStats = createAsyncThunk(
  'market/fetchSubmissionStats',
  async ({activeDays} = {}, {rejectWithValue}) => {
    try {
      const qs = activeDays ? `?active_days=${activeDays}` : '';
      const response = await axiosInstance.get(`/market/challenge/submission/stats/${qs}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch submission stats.');
    }
  }
);

export const fetchSubmissionActivity = createAsyncThunk(
  'market/fetchSubmissionActivity',
  async ({startDate, endDate} = {}, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const qs = params.toString();
      const response = await axiosInstance.get(
        `/market/challenge/submission/activity/${qs ? `?${qs}` : ''}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch submission activity.');
    }
  }
);

export const fetchChallengeId = createAsyncThunk(
  'market/fetchChallengeId',
  async ({marketSessionId, resourceId, useCase}, {rejectWithValue}) => {
    try {
      const query = `?market_session=${marketSessionId}&resource=${resourceId}&use_case=${useCase}`;
      const response = await axiosInstance.get(`/market/challenge/${query}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch challenge ID.');
    }
  }
);

// marketSlice.js (excerpt)

export const fetchMarketForecasts = createAsyncThunk(
  'market/fetchMarketForecasts',
  async ({
           startDate,
           endDate,
           resourceId,
           model = 'weighted_avg',
           quantiles = ['q10', 'q50', 'q90'],
           horizonFilter = 'latest',
         }, {rejectWithValue}) => {
    try {
      const mStart = moment.utc(startDate).format('YYYY-MM-DD');
      const mEnd = moment.utc(endDate).format('YYYY-MM-DD');
      const finalStart = `${mStart}T00:00:00Z`;
      const finalEnd = `${mEnd}T23:59:59Z`;
      const horizonParam = `&horizon=${encodeURIComponent(horizonFilter)}`;

      const fetchOneQuantile = async (variable) => {
        const items = [];
        let query = `?start_date=${finalStart}&end_date=${finalEnd}`
          + `&resource=${resourceId}&model=${model}&variable=${variable}`
          + horizonParam;
        query = appendLatestSessionOnlyParam(query, horizonFilter);

        while (query) {
          const finalUrl = query.startsWith('http')
            ? query
            : `/data/market-forecasts/${query}`;

          const response = await axiosInstance.get(finalUrl);
          const data = response.data.data;

          if (Array.isArray(data.results)) {
            items.push(...data.results.map(item => ({...item, variable})));
          }

          query = data.next;
        }
        return items;
      };

      const quantileResults = await Promise.all(quantiles.map(fetchOneQuantile));
      return quantileResults.flat();
    } catch (error) {
      console.error('Failed to fetch market forecasts:', error);
      return rejectWithValue('Failed to fetch market forecasts.');
    }
  }
);

export const fetchHorizonComparison = createAsyncThunk(
  'market/fetchHorizonComparison',
  async ({resource, startDate, endDate, horizonOffset, models, userIds}, {rejectWithValue}) => {
    try {
      const qs = buildHorizonCompareQuery({
        resource, startDate, endDate, horizonOffset,
        models: models || [], userIds: userIds || [],
      });
      const response = await axiosInstance.get(
        `/market/daily-scores/compare/range?${qs}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch horizon comparison.');
    }
  }
);

export const fetchResourceParticipantsRange = createAsyncThunk(
  'market/fetchResourceParticipantsRange',
  async ({startDate, endDate, resourceId}, {rejectWithValue}) => {
    try {
      const params = new URLSearchParams();
      params.set('resource', resourceId);
      params.set('start_date', startDate);
      params.set('end_date', endDate);
      const response = await axiosInstance.get(
        `/market/challenge/participants-range?${params.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch resource participants range.');
    }
  }
);

export const fetchComparisonForecasts = createAsyncThunk(
  'market/fetchComparisonForecasts',
  async ({ startDate, endDate, resourceId, models, horizonFilter = 'latest' }, { rejectWithValue }) => {
    try {
      const mStart = moment.utc(startDate).format('YYYY-MM-DD');
      const mEnd = moment.utc(endDate).format('YYYY-MM-DD');
      const finalStart = `${mStart}T00:00:00Z`;
      const finalEnd = `${mEnd}T23:59:59Z`;
      const horizonParam = `&horizon=${encodeURIComponent(horizonFilter)}`;

      const fetchOneModel = async (modelValue) => {
        try {
          const items = [];
          let query = `?start_date=${finalStart}&end_date=${finalEnd}`
            + `&resource=${resourceId}&model=${modelValue}&variable=q50`
            + horizonParam;
          query = appendLatestSessionOnlyParam(query, horizonFilter);

          while (query) {
            const finalUrl = query.startsWith('http') ? query : `/data/market-forecasts/${query}`;
            const response = await axiosInstance.get(finalUrl);
            const data = response.data.data;

            if (Array.isArray(data.results)) {
              items.push(...data.results.map(item => ({ ...item, variable: 'q50' })));
            }

            query = data.next;
          }
          return { model: modelValue, items };
        } catch (err) {
          return { model: modelValue, items: [] };
        }
      };

      const modelResults = await Promise.all(models.map(fetchOneModel));
      const result = {};
      modelResults.forEach(({ model: m, items }) => { result[m] = items; });
      return result;
    } catch (error) {
      return rejectWithValue('Failed to fetch comparison forecasts.');
    }
  }
);

export const fetchChallengeByDate = createAsyncThunk(
  'market/fetchChallengeByDate',
  async ({startDate, endDate, resourceId, useCase}, {rejectWithValue}) => {
    try {
      const query = `?resource=${resourceId}&use_case=${useCase}&start_date=${startDate}&end_date=${endDate}`;
      const response = await axiosInstance.get(`/market/challenge/${query}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch challenge ID.');
    }
  }
);

export const fetchMarketSession = createAsyncThunk(
  'market/fetchMarketSession',
  async ({marketSession, status, resource, latestOnly}, {rejectWithValue}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (marketSession) {
        // e.g. ?market_session=123
        queryParams.append('market_session', marketSession);
      }
      if (status) {
        // e.g. ?status=active
        queryParams.append('status', status);
      }
      if (latestOnly) {
        // e.g. ?latest_only=true
        // Or convert boolean to string if needed
        queryParams.append('latest_only', 'true');
      }
      if (resource) {
        queryParams.append('resource', resource);
      }

      // Construct final URL
      let url = '/market/session/';
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await axiosInstance.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch market session.');
    }
  }
);

export const fetchMarketSessionHistory = createAsyncThunk(
  'market/fetchMarketSessionHistory',
  async ({
    resourceId,
    page = 1,
    pageSize = 10,
    search = '',
    date = '',
    status = 'finished',
  }, {rejectWithValue}) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('resource', resourceId);
      queryParams.append('page', String(page));
      queryParams.append('page_size', String(pageSize));

      if (status) {
        queryParams.append('status', status);
      }

      if (search && search.trim()) {
        queryParams.append('search', search.trim());
      }

      if (date && date.trim()) {
        queryParams.append('date', date.trim());
      }

      const response = await axiosInstance.get(
        `/market/session-history/?${queryParams.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch market session history.');
    }
  }
);

// Recent target days (date-less listing, grouped by stored target_day). Kept
// separate from fetchMarketSessionHistory — which, once a day is selected, is
// narrowed to that day's coverage union — so the recent-days strip always has
// the full multi-day list regardless of the current selection.
export const fetchSessionDayList = createAsyncThunk(
  'market/fetchSessionDayList',
  async ({ resourceId, pageSize = 60, status = 'finished' }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('resource', resourceId);
      queryParams.append('page', '1');
      queryParams.append('page_size', String(pageSize));
      if (status) queryParams.append('status', status);
      const response = await axiosInstance.get(
        `/market/session-history/?${queryParams.toString()}`
      );
      return response.data.data?.results || [];
    } catch (error) {
      return rejectWithValue('Failed to fetch session day list.');
    }
  }
);

// Fetch pre-aggregated forecasts for a target day (latest per forecaster)
export const fetchTargetDayForecasts = createAsyncThunk(
  'market/fetchTargetDayForecasts',
  async ({ resourceId, targetDay, challengeId, model = 'weighted_avg' }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('resource', resourceId);
      queryParams.append('target_day', targetDay);
      queryParams.append('model', model);
      if (challengeId) queryParams.append('challenge', challengeId);

      const response = await axiosInstance.get(
        `/market/target-day-forecasts/?${queryParams.toString()}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch target day forecasts.');
    }
  }
);

// Fetch challenge status for a specific resource in the current session
export const fetchResourceChallengeStatus = createAsyncThunk(
  'market/fetchResourceChallengeStatus',
  async ({ sessionId, resourceId }, { rejectWithValue }) => {
    try {
      if (!sessionId || !resourceId) {
        return {
          resourceId: null,
          hasChallenge: false,
          challenges: [],
        };
      }

      const response = await axiosInstance.get(
        `/market/challenge/?market_session=${sessionId}&resource=${resourceId}`
      );
      const challenges = response.data.data || [];
      const sorted = [...challenges].sort((a, b) => (a.horizon_offset ?? 0) - (b.horizon_offset ?? 0));

      return {
        resourceId,
        hasChallenge: challenges.length > 0,
        challenges: sorted.map(c => ({
          id: c.id,
          challengeType: c.challenge_type || 'day_ahead',
          challengeTypeLabel: c.challenge_type_label || (c.challenge_type === 'intraday' ? 'Intraday' : 'Day-ahead'),
          horizonLabel: c.horizon || formatHorizonLabel(c.horizon_offset, c.challenge_type),
          horizonOffset: c.horizon_offset,
          targetDay: c.target_day,
          startDatetime: c.start_datetime,
          endDatetime: c.end_datetime,
        })),
      };
    } catch (error) {
      // Silently fail - don't break the UI for this enhancement
      return {
        resourceId,
        hasChallenge: false,
        challenges: [],
      };
    }
  }
);

// Fetch current open gate status for the dashboard indicator
export const fetchActiveSessionCounts = createAsyncThunk(
  'market/fetchActiveSessionCounts',
  async (_, {rejectWithValue}) => {
    try {
      // Fetch the latest open session so the indicator reflects the current gate.
      const response = await axiosInstance.get('/market/session/?status=open&latest_only=true');
      const sessions = response.data.data || [];

      if (sessions.length === 0) {
        return {
          isOpen: false,
          sessionId: null,
          challengeCount: 0,
          gateClosureAt: null,
        };
      }

      const latestSession = sessions[0];

      // Fetch challenges for this session to get count
      let challengeCount = 0;
      try {
        const challengeResponse = await axiosInstance.get(`/market/challenge/?market_session=${latestSession.id}`);
        const challenges = challengeResponse.data.data || [];
        challengeCount = Array.isArray(challenges) ? challenges.length : 0;
      } catch {
        // If challenge fetch fails, just show 0
        challengeCount = 0;
      }

      return {
        isOpen: latestSession.status === 'open',
        sessionId: latestSession.id,
        challengeCount,
        gateClosureAt: latestSession.gate_closure || null,
      };
    } catch (error) {
      return rejectWithValue('Failed to fetch active session status.');
    }
  }
);

// All challenges of the open session across every resource — feeds the
// cross-market submission overview (StatusHero rail + header selector dots).
export const fetchSessionChallengeList = createAsyncThunk(
  'market/fetchSessionChallengeList',
  async ({ sessionId }, { rejectWithValue }) => {
    try {
      if (!sessionId) {
        return { sessionId: null, challenges: [] };
      }
      const response = await axiosInstance.get(`/market/challenge/?market_session=${sessionId}`);
      const challenges = response.data.data || [];
      return {
        sessionId,
        challenges: challenges.map((c) => ({
          id: c.id,
          resourceId: c.resource,
          resourceName: c.resource_name,
        })),
      };
    } catch (error) {
      return rejectWithValue('Failed to fetch session challenge list.');
    }
  },
  {
    // Skip dispatching when a request for the same sessionId is already in
    // flight, so multiple consumers of useMarketsOverview() mounting in the
    // same commit don't each fire their own network request. A different
    // sessionId is never blocked.
    condition: ({sessionId}, {getState}) => {
      const pendingId = getState().market.sessionChallengeListPendingId;
      return pendingId == null || pendingId !== sessionId;
    },
  }
);

// Per-challenge coverage for the open session: ready=true when either an
// explicit session submission (all required quantiles) exists, or a continuous
// forecast fully covers the challenge window for all required quantiles.
export const fetchSessionCoverage = createAsyncThunk(
  'market/fetchSessionCoverage',
  async (_, {rejectWithValue}) => {
    try {
      const response = await axiosInstance.get('/market/session/coverage');
      const payload = response.data?.data || {};
      const map = {};
      (payload.coverage || []).forEach((row) => {
        map[row.challenge_id] = {ready: !!row.ready, source: row.source || null};
      });
      return {sessionId: payload.session_id ?? null, byChallenge: map};
    } catch (error) {
      return rejectWithValue('Failed to fetch session coverage.');
    }
  }
);

export const openMarketSession = createAsyncThunk(
  'market/openMarketSession',
  async ({ gateClosureHour = 10 } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/market/session/', {
        gate_closure_hour: gateClosureHour,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Failed to open market session.'
      );
    }
  }
);

// Forecaster-facing projection of upcoming challenge openings. The future
// schedule lives only in active templates (challenges aren't materialized until
// their session opens), so the backend computes it on the fly from templates.
export const fetchUpcomingSchedule = createAsyncThunk(
  'market/fetchUpcomingSchedule',
  async ({ windowHours, resourceId, useCase, horizon } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (windowHours) params.append('window_hours', String(windowHours));
      if (resourceId) params.append('resource', resourceId);
      if (useCase) params.append('use_case', useCase);
      if (horizon) params.append('horizon', horizon);
      const qs = params.toString();
      const response = await axiosInstance.get(
        `/market/upcoming-schedule/${qs ? `?${qs}` : ''}`
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch upcoming schedule.');
    }
  }
);

const marketSlice = createSlice({
  name: 'market',
  initialState: {
    marketResources: [],
    // dashboard download report state
    model: getPreferredModel(),
    includeEnsemble: false,
    anonymize: false,
    // Active session status for the dashboard gate indicator
    activeSessionStatus: { isOpen: false, sessionId: null, challengeCount: 0, gateClosureAt: null },
    activeSessionStatusLoading: false,
    openMarketSessionLoading: false,
    // Resource-specific challenge status for selected resource
    resourceChallengeStatus: {
      resourceId: null,
      hasChallenge: false,
      challenges: [],
    },
    resourceChallengeStatusLoading: false,
    // Comparison models
    comparisonModels: [],
    comparisonForecasts: {},
    comparisonForecastsLoading: false,
    horizonComparison: null,
    horizonComparisonLoading: false,
    horizonComparisonError: null,
    resourceParticipantsRange: [],
    resourceParticipantsRangeLoading: false,
    // Available horizons for the selected resource
    availableHorizons: [],
    availableHorizonsLoading: false,
    // Per-challenge submission status for the current session (forecaster dashboard)
    challengeSubmissions: {},
    challengeSubmissionsLoading: false,
    // Per-challenge coverage (session_submission | continuous_forecast)
    sessionCoverage: {sessionId: null, byChallenge: {}},
    sessionCoverageLoading: false,
    // All challenges of the open session, across resources (cross-market overview)
    sessionChallengeList: {sessionId: null, challenges: []},
    sessionChallengeListLoading: false,
    // sessionId of the in-flight fetchSessionChallengeList request, if any;
    // lets the thunk's condition dedupe identical concurrent requests from
    // multiple useMarketsOverview() consumers mounting in the same commit.
    sessionChallengeListPendingId: null,
    // Forecast horizon selection for the main dashboard chart.
    horizonFilter: 'latest',
    marketScoreboard: null,
    scoreBoardLoading: false,
    scoreBoardError: false,
    sessionScoreboard: null,
    challenge: null,
    lastFetchSignature: null,
    // session forecasts state
    userSessionSelected: null,
    userSessionForecast: [],
    userSessionForecastLoading: false,
    userSessionForecastError: false,
    ensembleForecasts: [],
    ensembleForecastsLoading: false,
    ensembleSessionForecast: [],// store all ensemble forecasts given date range
    loading: false,
    marketSessions: [],
    totalSubmissions: 0,
    activeForecasters: 0,
    activeUsers: 0,
    submissionActivity: null,
    sessionHistory: { count: 0, next: null, previous: null, results: [] },
    sessionHistoryLoading: false,
    sessionHistoryError: false,
    // Recent target days for the Forecasts strip — fetched independently of the
    // selected-day coverage view so the two never clobber each other.
    sessionDayList: [],
    sessionDayListLoading: false,
    // Selected target day on the Forecasts page, persisted across navigation.
    selectedTargetDay: null,
    selectedSession: null,
    targetDayForecasts: null,
    targetDayForecastsLoading: false,
    // Projected schedule of upcoming challenge openings (rhythm + next openings)
    upcomingSchedule: { patterns: [], openings: [], windowHours: 48, generatedAt: null },
    upcomingScheduleLoading: false,
    upcomingScheduleError: null,
    // Default view: 5 days behind today through the day-ahead (today + 1).
    startDate: moment().utc().subtract(5, 'days').startOf('day').format('YYYY-MM-DD'),
    endDate: moment().utc().add(1, 'days').startOf('day').format('YYYY-MM-DD'),
    errorMessage: '',
  },
  reducers: {
    // Set the last fetched key in the dashboard ensemble forecasts
    clearSessionScoreboard(state) {
      state.sessionScoreboard = null;
    },
    clearMarketScoreboard(state) {
      state.marketScoreboard = null;
    },
    setUserSessionSelected(state, action) {
      state.userSessionSelected = action.payload;
    },
    setModel(state, action) {
      const {model} = action.payload;
      state.model = model;
    },
    setIncludeEnsemble(state, action) {
      const {includeEnsemble} = action.payload;
      state.includeEnsemble = includeEnsemble;
    },
    setAnonymize(state, action) {
      const {anonymize} = action.payload;
      state.anonymize = anonymize;
    },
    setLastFetchedKey(state, action) {
      const {resourceId, startDate, endDate} = action.payload;
      state.lastFetchedResourceId = resourceId;
      state.lastFetchedStartDate = startDate;
      state.lastFetchedEndDate = endDate;
    },
    clearMarketError(state) {
      state.errorMessage = '';
    },
    setDateRange(state, action) {
      const {startDate, endDate} = action.payload;
      state.startDate = startDate;
      state.endDate = endDate;
    },
    setClearEnsembleForecasts(state) {
      state.ensembleForecasts = null
    },
    clearMarketSessions(state) {
      state.marketSessions = [];
    },
    clearSessionHistory(state) {
      state.sessionHistory = { count: 0, next: null, previous: null, results: [] };
      state.sessionHistoryLoading = false;
      state.sessionHistoryError = false;
    },
    clearSessionDayList(state) {
      state.sessionDayList = [];
      state.sessionDayListLoading = false;
    },
    setSelectedTargetDay(state, action) {
      state.selectedTargetDay = action.payload;
    },
    setSelectedSession(state, action) {
      state.selectedSession = action.payload;
    },
    clearEnsembleSessionForecast(state) {
      state.ensembleSessionForecast = null;
    },
    clearUserSessionForecast(state) {
      state.userSessionForecast = null;
    },
    setLastFetchSignature(state, action) {
      state.lastFetchSignature = action.payload;
    },
    clearLastFetchSignature(state) {
      state.lastFetchSignature = null;
    },
    clearChallenge(state) {
      state.challenge = null
    },
    setHorizonFilter(state, action) {
      state.horizonFilter = action.payload;
    },
    setComparisonModels(state, action) {
      state.comparisonModels = action.payload;
    },
    clearComparisonForecasts(state) {
      state.comparisonForecasts = {};
    },
    clearResourceParticipantsRange(state) {
      state.resourceParticipantsRange = [];
      state.resourceParticipantsRangeLoading = false;
    },
    clearTargetDayForecasts(state) {
      state.targetDayForecasts = null;
      state.targetDayForecastsLoading = false;
    },
    clearChallengeSubmissions(state) {
      state.challengeSubmissions = {};
      state.challengeSubmissionsLoading = false;
    },
    clearSessionCoverage(state) {
      state.sessionCoverage = {sessionId: null, byChallenge: {}};
      state.sessionCoverageLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchSessionScoreboard actions
      .addCase(fetchScoreboard.pending, (state) => {
        state.scoreBoardLoading = true;
        state.errorMessage = '';
      })
      .addCase(fetchScoreboard.fulfilled, (state, action) => {
        state.scoreBoardLoading = false;
        const {marketSessionId} = action.meta.arg;
        if (marketSessionId) {
          state.sessionScoreboard = action.payload.data;
        } else {
          state.marketScoreboard = action.payload.data;
        }
      })
      .addCase(fetchScoreboard.rejected, (state, action) => {
        state.scoreBoardError = true;
        state.scoreBoardLoading = false;
        state.errorMessage = action.payload || 'Failed to fetch session scoreboard.';
      })
      // Handle fetchEnsembleForecasts actions
      .addCase(fetchEnsembleForecasts.pending, (state) => {
        state.ensembleForecastsLoading = true;
        state.errorMessage = '';
      })
      .addCase(fetchEnsembleForecasts.fulfilled, (state, action) => {
        state.ensembleForecastsLoading = false;
        state.ensembleSessionForecast = action.payload;
      })
      .addCase(fetchEnsembleForecasts.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to fetch ensemble forecasts.';
      })
      .addCase(fetchLatestMarketSession.fulfilled, (state, action) => {
        state.marketSession = action.payload.data;
      })
      .addCase(fetchLatestMarketSession.rejected, (state, action) => {
        state.errorMessage = action.payload || 'Failed to fetch latest market session.';
      })
      .addCase(fetchSubmissionStats.fulfilled, (state, action) => {
        state.totalSubmissions = action.payload?.total_submissions ?? 0;
        state.activeForecasters = action.payload?.active_forecasters ?? 0;
        state.activeUsers = action.payload?.active_users ?? 0;
      })
      .addCase(fetchSubmissionStats.rejected, (state, action) => {
        state.errorMessage = action.payload || 'Failed to fetch submission stats.';
      })
      .addCase(fetchSubmissionActivity.fulfilled, (state, action) => {
        state.submissionActivity = action.payload;
      })
      .addCase(fetchSubmissionActivity.rejected, (state, action) => {
        state.errorMessage = action.payload || 'Failed to fetch submission activity.';
      })
      .addCase(fetchChallengeId.fulfilled, (state, action) => {
        state.loading = false;
        state.challengeId = action.payload.data;
      })
      .addCase(fetchChallengeId.rejected, (state, action) => {
        state.errorMessage = action.payload || 'Failed to fetch challenge ID.';
      })
      .addCase(fetchMarketForecasts.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchMarketForecasts.fulfilled, (state, action) => {
        state.loading = false;
        // The thunk fetches all quantiles internally and returns them combined
        // in a single payload, so replace (like rawData) instead of appending.
        // Appending let stale responses from an overlapping fetch cycle survive
        // inconsistently vs rawData, blanking the ensemble on resource switch.
        state.ensembleForecasts = action.payload;
      })
      // 3) REJECTED
      .addCase(fetchMarketForecasts.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to fetch market forecasts.';
      })
      .addCase(fetchMarketSession.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchMarketSession.fulfilled, (state, action) => {
        state.loading = false;
        state.marketSessions = action.payload;
      })
      .addCase(fetchMarketSession.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to fetch market session.';
      })
      .addCase(fetchMarketSessionHistory.pending, (state) => {
        state.sessionHistoryLoading = true;
        state.sessionHistoryError = false;
      })
      .addCase(fetchMarketSessionHistory.fulfilled, (state, action) => {
        state.sessionHistoryLoading = false;
        state.sessionHistory = action.payload || {
          count: 0,
          next: null,
          previous: null,
          results: [],
        };
      })
      .addCase(fetchMarketSessionHistory.rejected, (state, action) => {
        state.sessionHistoryLoading = false;
        state.sessionHistoryError = true;
        state.errorMessage = action.payload || 'Failed to fetch market session history.';
      })
      .addCase(fetchSessionDayList.pending, (state) => {
        state.sessionDayListLoading = true;
      })
      .addCase(fetchSessionDayList.fulfilled, (state, action) => {
        state.sessionDayListLoading = false;
        state.sessionDayList = action.payload || [];
      })
      .addCase(fetchSessionDayList.rejected, (state) => {
        state.sessionDayListLoading = false;
      })
      .addCase(fetchTargetDayForecasts.pending, (state) => {
        state.targetDayForecastsLoading = true;
      })
      .addCase(fetchTargetDayForecasts.fulfilled, (state, action) => {
        state.targetDayForecastsLoading = false;
        state.targetDayForecasts = action.payload;
      })
      .addCase(fetchTargetDayForecasts.rejected, (state, action) => {
        state.targetDayForecastsLoading = false;
        state.errorMessage = action.payload || 'Failed to fetch target day forecasts.';
      })
      .addCase(fetchMarketSessionChallenge.fulfilled, (state, action) => {
        state.loading = false;
        state.challenge = action.payload;
      })
      .addCase(fetchMarketSessionChallenge.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload || 'Failed to fetch challenge ID.';
      })
      .addCase(fetchChallengeByDate.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(fetchSessionForecastSubmission.fulfilled, (state, action) => {
        state.userSessionForecastLoading = false;
        state.userSessionForecast = action.payload;
      })
      .addCase(fetchSessionForecastSubmission.rejected, (state, action) => {
        state.loading = false;
        state.userSessionForecastError = true;
        state.errorMessage = action.payload || 'Failed to fetch session forecast submission.';
      })
      .addCase(fetchSessionForecastSubmission.pending, (state, action) => {
        state.userSessionForecastLoading = true;
        state.errorMessage = action.payload || 'Failed to fetch session forecast submission.';
      })
      .addCase(fetchMarketResources.fulfilled, (state, action) => {
        state.marketResources = action.payload;
      })
      .addCase(fetchMarketResources.rejected, (state, action) => {
        state.errorMessage = action.payload || 'Failed to fetch market resources.';
      })
      // Handle fetchActiveSessionCounts actions
      .addCase(fetchActiveSessionCounts.pending, (state) => {
        state.activeSessionStatusLoading = true;
      })
      .addCase(fetchActiveSessionCounts.fulfilled, (state, action) => {
        state.activeSessionStatusLoading = false;
        state.activeSessionStatus = action.payload;
      })
      .addCase(fetchActiveSessionCounts.rejected, (state) => {
        state.activeSessionStatusLoading = false;
      })
      .addCase(openMarketSession.pending, (state) => {
        state.openMarketSessionLoading = true;
      })
      .addCase(openMarketSession.fulfilled, (state) => {
        state.openMarketSessionLoading = false;
      })
      .addCase(openMarketSession.rejected, (state, action) => {
        state.openMarketSessionLoading = false;
        state.errorMessage = action.payload || 'Failed to open market session.';
      })
      // Handle fetchResourceChallengeStatus actions
      .addCase(fetchResourceChallengeStatus.pending, (state) => {
        state.resourceChallengeStatusLoading = true;
      })
      .addCase(fetchResourceChallengeStatus.fulfilled, (state, action) => {
        state.resourceChallengeStatusLoading = false;
        state.resourceChallengeStatus = action.payload;
      })
      .addCase(fetchResourceChallengeStatus.rejected, (state) => {
        state.resourceChallengeStatusLoading = false;
      })
      .addCase(fetchComparisonForecasts.pending, (state) => {
        state.comparisonForecastsLoading = true;
      })
      .addCase(fetchComparisonForecasts.fulfilled, (state, action) => {
        state.comparisonForecastsLoading = false;
        state.comparisonForecasts = action.payload;
      })
      .addCase(fetchComparisonForecasts.rejected, (state) => {
        state.comparisonForecastsLoading = false;
      })
      .addCase(fetchHorizonComparison.pending, (state) => {
        state.horizonComparisonLoading = true;
        state.horizonComparisonError = null;
      })
      .addCase(fetchHorizonComparison.fulfilled, (state, action) => {
        state.horizonComparisonLoading = false;
        state.horizonComparison = action.payload;
      })
      .addCase(fetchHorizonComparison.rejected, (state, action) => {
        state.horizonComparisonLoading = false;
        state.horizonComparisonError = action.payload || 'error';
      })
      .addCase(fetchResourceParticipantsRange.pending, (state) => {
        state.resourceParticipantsRangeLoading = true;
      })
      .addCase(fetchResourceParticipantsRange.fulfilled, (state, action) => {
        state.resourceParticipantsRangeLoading = false;
        state.resourceParticipantsRange = action.payload || [];
      })
      .addCase(fetchResourceParticipantsRange.rejected, (state) => {
        state.resourceParticipantsRangeLoading = false;
      })
      .addCase(fetchAvailableHorizons.pending, (state) => {
        state.availableHorizonsLoading = true;
      })
      .addCase(fetchAvailableHorizons.fulfilled, (state, action) => {
        state.availableHorizonsLoading = false;
        state.availableHorizons = action.payload || [];
        if (Array.isArray(action.payload) && action.payload.length > 0) {
          const maxHorizon = Math.max(...action.payload);
          try {
            const prefs = JSON.parse(localStorage.getItem('userPreferences') || '{}');
            localStorage.setItem('userPreferences', JSON.stringify({ ...prefs, maxHorizon }));
          } catch (err) {}
        }
      })
      .addCase(fetchAvailableHorizons.rejected, (state) => {
        state.availableHorizonsLoading = false;
      })
      .addCase(fetchChallengeSubmissions.pending, (state) => {
        state.challengeSubmissionsLoading = true;
      })
      .addCase(fetchChallengeSubmissions.fulfilled, (state, action) => {
        state.challengeSubmissionsLoading = false;
        const map = {};
        for (const item of action.payload) {
          map[item.challengeId] = { submitted: item.submitted, quantiles: item.quantiles };
        }
        state.challengeSubmissions = map;
      })
      .addCase(fetchSessionChallengeList.pending, (state, action) => {
        state.sessionChallengeListLoading = true;
        state.sessionChallengeListPendingId = action.meta.arg.sessionId;
      })
      .addCase(fetchSessionChallengeList.fulfilled, (state, action) => {
        // A superseded request (session changed while this one was still in
        // flight) resolving after a newer one started: its sessionId no
        // longer matches the pending id, so ignore it rather than clobber
        // the newer request's state with stale data.
        if (action.meta.arg.sessionId !== state.sessionChallengeListPendingId) return;
        state.sessionChallengeListLoading = false;
        state.sessionChallengeList = action.payload;
        state.sessionChallengeListPendingId = null;
      })
      .addCase(fetchSessionChallengeList.rejected, (state, action) => {
        if (action.meta.arg.sessionId !== state.sessionChallengeListPendingId) return;
        state.sessionChallengeListLoading = false;
        state.sessionChallengeList = {sessionId: null, challenges: []};
        state.sessionChallengeListPendingId = null;
      })
      .addCase(fetchSessionCoverage.pending, (state) => {
        state.sessionCoverageLoading = true;
      })
      .addCase(fetchSessionCoverage.fulfilled, (state, action) => {
        state.sessionCoverageLoading = false;
        state.sessionCoverage = action.payload;
      })
      .addCase(fetchSessionCoverage.rejected, (state) => {
        state.sessionCoverageLoading = false;
        state.sessionCoverage = {sessionId: null, byChallenge: {}};
      })
      .addCase(fetchChallengeSubmissions.rejected, (state) => {
        state.challengeSubmissionsLoading = false;
      })
      .addCase(fetchUpcomingSchedule.pending, (state) => {
        state.upcomingScheduleLoading = true;
        state.upcomingScheduleError = null;
      })
      .addCase(fetchUpcomingSchedule.fulfilled, (state, action) => {
        state.upcomingScheduleLoading = false;
        const data = action.payload || {};
        state.upcomingSchedule = {
          patterns: data.patterns || [],
          openings: data.openings || [],
          windowHours: data.window_hours ?? 48,
          generatedAt: data.generated_at ?? null,
        };
      })
      .addCase(fetchUpcomingSchedule.rejected, (state, action) => {
        state.upcomingScheduleLoading = false;
        state.upcomingScheduleError = action.payload || 'Failed to fetch upcoming schedule.';
      })
  },
});

export const {
  clearUserSessionForecast,
  setDateRange,
  setClearEnsembleForecasts,
  setLastFetchedKey,
  setSelectedSession,
  setUserSessionSelected,
  clearSessionScoreboard,
  clearMarketScoreboard,
  clearEnsembleSessionForecast,
  clearChallenge,
  clearMarketError,
  clearMarketSessions,
  clearSessionHistory,
  setModel,
  setIncludeEnsemble,
  setAnonymize,
  setLastFetchSignature,
  clearLastFetchSignature,
  setHorizonFilter,
  setComparisonModels,
  clearComparisonForecasts,
  clearResourceParticipantsRange,
  clearTargetDayForecasts,
  clearChallengeSubmissions,
  clearSessionCoverage,
  clearSessionDayList,
  setSelectedTargetDay,
} = marketSlice.actions;
export default marketSlice.reducer;

// Selectors
export const selectSessionScoreboard = (state) => state.market.sessionScoreboard;
export const selectMarketScoreboard = (state) => state.market.marketScoreboard;
export const selectEnsembleForecasts = (state) => state.market.ensembleForecasts;
export const selectEnsembleSessionForecast = (state) => state.market.ensembleSessionForecast;
export const selectChallengeId = (state) => state.user.challengeId;
export const selectMarketLoading = (state) => state.market.loading;
export const selectMarketError = (state) => state.market.errorMessage;
export const selectMarketStartDate = (state) => state.market.startDate;
export const selectMarketEndDate = (state) => state.market.endDate;
export const selectMarketSessions = (state) => state.market.marketSessions;
export const selectTotalSubmissions = (state) => state.market.totalSubmissions;
export const selectActiveForecasters = (state) => state.market.activeForecasters;
export const selectMarketActiveUsers = (state) => state.market.activeUsers;
export const selectSubmissionActivity = (state) => state.market.submissionActivity;
export const selectSessionHistory = (state) => state.market.sessionHistory;
export const selectSessionHistoryLoading = (state) => state.market.sessionHistoryLoading;
export const selectSessionDayList = (state) => state.market.sessionDayList;
export const selectSessionDayListLoading = (state) => state.market.sessionDayListLoading;
export const selectSelectedTargetDay = (state) => state.market.selectedTargetDay;
export const selectedSession = (state) => state.market.selectedSession;
export const selectMarketSessionChallenge = (state) => state.market.challenge;
export const selectUserSessionForecastSubmission = (state) => state.market.userSessionForecast;
export const selectScoreBoardLoading = (state) => state.market.scoreBoardLoading;
export const selectUserForecastSubmissionLoading = (state) => state.market.userSessionForecastLoading;
export const selectUserSessionSelected = (state) => state.market.userSessionSelected;
export const selectDownloadReportLoading = (state) => state.market.downloadReportLoading;
export const selectModel = (state) => state.market.model;
export const selectIncludeEnsemble = (state) => state.market.includeEnsemble;
export const selectAnonymize = (state) => state.market.anonymize;
export const selectMarketResources = (state) => state.market.marketResources;
export const selectActiveSessionStatus = (state) => state.market.activeSessionStatus;
export const selectActiveSessionStatusLoading = (state) => state.market.activeSessionStatusLoading;
export const selectOpenMarketSessionLoading = (state) => state.market.openMarketSessionLoading;
export const selectHorizonFilter = (state) => state.market.horizonFilter;
export const selectResourceChallengeStatus = (state) => state.market.resourceChallengeStatus;
export const selectResourceChallengeStatusLoading = (state) => state.market.resourceChallengeStatusLoading;
export const selectComparisonModels = (state) => state.market.comparisonModels;
export const selectComparisonForecasts = (state) => state.market.comparisonForecasts;
export const selectResourceParticipantsRange = (state) => state.market.resourceParticipantsRange;
export const selectResourceParticipantsRangeLoading = (state) => state.market.resourceParticipantsRangeLoading;
export const selectTargetDayForecasts = (state) => state.market.targetDayForecasts;
export const selectTargetDayForecastsLoading = (state) => state.market.targetDayForecastsLoading;
export const selectAvailableHorizons = (state) => state.market.availableHorizons;
export const selectAvailableHorizonsLoading = (state) => state.market.availableHorizonsLoading;
export const selectChallengeSubmissions = (state) => state.market.challengeSubmissions;
export const selectChallengeSubmissionsLoading = (state) => state.market.challengeSubmissionsLoading;
export const selectSessionCoverage = (state) => state.market.sessionCoverage;
export const selectSessionCoverageLoading = (state) => state.market.sessionCoverageLoading;
export const selectSessionChallengeList = (state) => state.market.sessionChallengeList;
export const selectHorizonComparison = (state) => state.market.horizonComparison;
export const selectHorizonComparisonLoading = (state) => state.market.horizonComparisonLoading;
export const selectHorizonComparisonError = (state) => state.market.horizonComparisonError;
export const selectUpcomingSchedule = (state) => state.market.upcomingSchedule;
export const selectUpcomingScheduleLoading = (state) => state.market.upcomingScheduleLoading;
export const selectUpcomingScheduleError = (state) => state.market.upcomingScheduleError;
